/* eslint no-console: 0 */
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/styles';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { useCancelDeviceMutation } from '../../../hooks/useCancelDeviceMutation.ts';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';

const useStyles = makeStyles({
  snackbar: {
    position: 'absolute',
    bottom: 'calc(var(--app-safe-area-inset-bottom) + 64px)',
  },
});

const StyledContent = styled.div`
  p {
    margin-bottom: 20px;
  }
`;

const StyledActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  margin-top: 30px;
`;

interface AlertComponentProps extends AlertProps {}
const Alert: React.FC<AlertComponentProps> = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

interface DeviceServicePlanCancelRouteProps { }

interface ToastMessage {
  message: string;
  severity: 'error' | 'success';
}

export const DeviceServicePlanCancelRoute: React.FC<DeviceServicePlanCancelRouteProps> = () => {
  const classes = useStyles();
  const { id: deviceId } = useParams();

  const [ isCancelLoading, setIsCancelLoading ] = useState<boolean>(false);
  const isLoading = isCancelLoading;
  const history = useHistory();
  const queryClient = useQueryClient();
  const mutation = useCancelDeviceMutation();
  const [toastMessage, setToastMessage] = useState<ToastMessage | undefined>(undefined);
  const [ isComplete, setIsComplete ] = useState<boolean>(false);

  useSetModalTitle('Cancel Plan');

  const onHideToast = useCallback(() => {
    setToastMessage(undefined);
  }, []);

  const onCancelSubmit = useCallback(() => {
    setIsCancelLoading(true);
    mutation.mutate({ deviceId }, {
      onSuccess: () => {

        // Introduce a delay before this callback completes to hopefully give the backend
        // enough time to sync state with stripe.
        setTimeout(() => {      
          setIsCancelLoading(false);
          setIsComplete(true);
          queryClient.invalidateQueries({ queryKey: ['useGetDevice', deviceId] });
          queryClient.setQueryData(['useGetDevice', deviceId], null);

          setToastMessage({ 
            severity: 'success',
            message: 'Service plan successfully canceled.',
          });
        }, 3000);
      },
      onError: () => {
        setIsCancelLoading(false);
        setToastMessage({ 
          severity: 'error',
          message: 'Unable to cancel service plan.',
        });
      },
    });
  }, [mutation, deviceId]);
  
  const onCancelCancel = useCallback(() => {
    screenNavigator.exitCurrentScreen(history)
  }, [history]);

  return (
    <StyledContent>
      <div>
        <p>Are you sure you want to cancel your service plan for this device?</p>

        <p>If you cancel, your device will stop tracking, and after 30 days of inactivity, all historic location data will be permanently deleted.</p>

        <p>You can reactivate your device at any point in the future but please note that historic location data will be lost.</p>
      </div>

      <StyledActions>
        <Button
          onClick={onCancelSubmit}
          color="secondary"
          disabled={isLoading || isComplete}
          loading={isLoading}>Yes</Button>

        <Button
          onClick={onCancelCancel}
          color="primary"
          disabled={isLoading || isComplete}
        >No</Button>
      </StyledActions>

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={12000}
        onClose={onHideToast}
        className={classes.snackbar}
      >
        { !!toastMessage ? (
          <Alert onClose={onHideToast} severity={toastMessage?.severity}>
            {toastMessage?.message}
          </Alert> 
        ) : undefined}
      </Snackbar>
    </StyledContent>
  );
};
